import React from 'react';
import { twMerge } from 'tailwind-merge';
import MessageActionMenu from 'flow/components/MessageActionMenu';
import ThreadActionButton from 'flow/components/thread-messages/ThreadActionButton';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import { useAppDispatch } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import { Message, ThreadSummary } from 'flow/types/Chats';
import { TypedMap } from 'flow/types/TypedMap';

type HasFeature = ReturnType<typeof useFeatureFlags>['hasFeature'];

interface DropdownMenuControlsOptions {
    hasFeature: HasFeature;
    message: TypedMap<Message>;
    showContextMenu: boolean;
    isThreadEnabled: boolean;
    showReactions: boolean;
}

// The controls this menu renders for a message: the "More options" menu, or
// the Reply and thread buttons. The action bar works them out once, since it
// also needs them to decide whether it renders at all, and passes them down.
export const getDropdownMenuControls = ({
    hasFeature,
    message,
    showContextMenu,
    isThreadEnabled,
    showReactions,
}: DropdownMenuControlsOptions) => {
    const alsoAsSMS = message.get('alsoAsSMS');
    const menu =
        hasFeature([FeatureFlags.QUOTED_REPLIES, FeatureFlags.THREADS], true) &&
        !alsoAsSMS;

    return {
        menu,
        reply:
            !menu &&
            showContextMenu &&
            hasFeature(FeatureFlags.QUOTED_REPLIES) &&
            !alsoAsSMS,
        thread:
            !menu &&
            isThreadEnabled &&
            showContextMenu &&
            showReactions &&
            !alsoAsSMS,
    };
};

export type DropdownMenuControls = ReturnType<typeof getDropdownMenuControls>;

// Whether this menu renders anything for a message. The action bar asks,
// since a bar with no control does not render.
export const hasDropdownMenuControls = ({
    menu,
    reply,
    thread,
}: DropdownMenuControls) => menu || reply || thread;

interface ChatMessageDropdownMenuProps {
    /** What to render, from getDropdownMenuControls. */
    controls: DropdownMenuControls;
    message: TypedMap<Message>;
    sessionKey: string;
    openSelectedThreadMessage: Function;
    addQuotedMessage: Function;
    threadSummary: TypedMap<ThreadSummary>;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

const ChatMessageDropdownMenu: React.FC<ChatMessageDropdownMenuProps> = ({
    controls,
    message,
    sessionKey,
    openSelectedThreadMessage,
    addQuotedMessage,
    threadSummary,
    videoDark = false,
}) => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();

    if (controls.menu) {
        return (
            <MessageActionMenu
                hasThreadMessages={threadSummary?.getIn(['messageCount']) > 0}
                quotedRepliesAction={() =>
                    dispatch(
                        addQuotedMessage({
                            message,
                            sessionKey,
                        }),
                    )
                }
                threadAction={() =>
                    dispatch(
                        openSelectedThreadMessage(
                            sessionKey,
                            message.get('key'),
                        ),
                    )
                }
            />
        );
    }
    return (
        <>
            {controls.reply && (
                <div className="group-hover:flex">
                    <IconButton
                        tooltipLabel={i18next.t('message.input.reply')}
                        tooltipPlacement="bottom"
                        onClick={() =>
                            dispatch(
                                addQuotedMessage({
                                    message,
                                    sessionKey,
                                }),
                            )
                        }
                        className={twMerge(
                            'size-9 rounded-sm',
                            videoDark
                                ? 'text-gray-300 hover:bg-gray-600'
                                : 'text-gray-600 hover:bg-gray-100',
                        )}
                    >
                        <Icon iconKey="reply" size="1.3rem" />
                    </IconButton>
                </div>
            )}

            {controls.thread && (
                <ThreadActionButton
                    hasThreadMessages={
                        threadSummary?.getIn(['messageCount']) > 0
                    }
                    onThreadOpenClicked={() =>
                        dispatch(
                            openSelectedThreadMessage(
                                sessionKey,
                                message.get('key'),
                            ),
                        )
                    }
                />
            )}
        </>
    );
};

export default ChatMessageDropdownMenu;
